import React, { useState, useEffect, useRef, createContext } from 'react';
import './SvgZoomContext.css';
import { create } from 'zustand';
import { registry } from '../registryData';

interface TileState {
  selectedTileGroup: string | null;
  setSelectedTileGroup: (group: string | null) => void;
  isAnimating: boolean;
  setIsAnimating: (animating: boolean) => void;
}

export const useTileStore = create<TileState>((set) => ({
  selectedTileGroup: null,
  setSelectedTileGroup: (group) => set({ selectedTileGroup: group }),
  isAnimating: false,
  setIsAnimating: (animating) => set({ isAnimating: animating }),
}));

/* SVG Zoom Context */
type SvgZoomContext = {
  handleSpecialCircleClick: (zoomScale: number, translateX: number, translateY: number) => () => void;
  selectedTileGroup: string | null;
  setSelectedTileGroup: (groupName: string | null) => void;
  isAnimating: boolean;
  handleComponentClick: (event: React.MouseEvent) => void;
};

export const SvgZoomContext = createContext<SvgZoomContext | null>(null);

interface SvgZoomContextProviderProps {
  children: React.ReactNode;
  width?: number;
  height?: number;
}

/*RELATED TO SIDEBAR OVERLAY*/
const bias = 200;

export function SvgZoomContextProvider({ 
  children, 
  width = 400, 
  height = 400 
}: SvgZoomContextProviderProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const isAnimating = useTileStore((state) => state.isAnimating);
  const setIsAnimating = useTileStore((state) => state.setIsAnimating);
  const selectedTileGroup = useTileStore((state) => state.selectedTileGroup);
  const setSelectedTileGroup = useTileStore((state) => state.setSelectedTileGroup);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const translateXRef = useRef<number>(0);
  const translateYRef = useRef<number>(0);
  const zoomScaleRef = useRef<number>(1.0);

  /*
  HANDLE ANIMATED ZOOM AND PAN TO GROUPS
  */
  const handleSpecialCircleClickInternal = (zoomScale: number, translateX: number, translateY: number) => {
    // Prevent multiple clicks during animation
    if (isAnimating) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // CASE 1: Zoomed in and panning to a different location
    if (isZoomed && (translateX !== translateXRef.current || translateY !== translateYRef.current)) {
      console.log("zoom to other loc");
      if (svgContainerRef.current && translateXRef.current && translateYRef.current){
        svgContainerRef.current.style.setProperty('--zoom-scale-prev', zoomScaleRef.current.toString());
        svgContainerRef.current.style.setProperty('--zoom-translate-x-prev', `${viewportWidth/2 + bias - translateXRef.current*(viewportWidth/width)}px`);
        svgContainerRef.current.style.setProperty('--zoom-translate-y-prev', `${viewportHeight/2 - translateYRef.current*(viewportWidth/width)}px`);
        svgContainerRef.current.style.setProperty('--zoom-scale', zoomScale.toString());
        svgContainerRef.current.style.setProperty('--zoom-translate-x', `${viewportWidth/2 + bias - translateX*(viewportWidth/width)}px`);
        svgContainerRef.current.style.setProperty('--zoom-translate-y', `${viewportHeight/2 - translateY*(viewportWidth/width)}px`);
      }
      setAnimationClass('no-op'); // Clear animation class first to force browser to recognize new animation
      setTimeout(() => {
        setAnimationClass('zoomed-to-other');
      }, 10);
      // save latest X and Y coordinates
      zoomScaleRef.current = zoomScale;
      translateXRef.current = translateX;
      translateYRef.current = translateY;
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 1200);
      return;
    }
    // zoom in or out of the same location
  
    // save latest X and Y coordinates
    zoomScaleRef.current = zoomScale;
    translateXRef.current = translateX;
    translateYRef.current = translateY;

    // CASE 2: Zooming in
    if (!isZoomed) {
      if (svgContainerRef.current) {
        svgContainerRef.current.style.setProperty('--zoom-scale', zoomScale.toString());
        console.log(`vpcenter ${translateY*(viewportWidth/width)})`)
        svgContainerRef.current.style.setProperty('--zoom-translate-x', `${viewportWidth/2 +bias - translateX*(viewportWidth/width)}px`);
        svgContainerRef.current.style.setProperty('--zoom-translate-y', `${viewportHeight/2 - translateY*(viewportWidth/width)}px`);
        
        // Set transform-origin to viewport center
        svgContainerRef.current.style.setProperty('--transform-origin-x', `${viewportWidth/2 + bias }px`);
        svgContainerRef.current.style.setProperty('--transform-origin-y', `${viewportHeight/2}px`);
      }
      setAnimationClass('zoomed-to-special');
      setIsZoomed(true);
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 1200);
    
    // CASE 3: Zooming out
    } else {
      console.log("zooming out");
      setAnimationClass('zoom-out');
      setIsZoomed(false);
      setIsAnimating(true);
      setTimeout(() => {
        setAnimationClass('');
        setIsAnimating(false);
      }, 1200);
    } 
    
  };

  const handleSpecialCircleClick = (zoomScale: number, translateX: number, translateY: number) => {
    return () => handleSpecialCircleClickInternal(zoomScale, translateX, translateY);
  };

  /*
  DETECT IF A TILE (not part of a Group) IS CLICKED AND ZOOM OUT FROM CURRENT GROUP
  */
  const handleComponentClick = (event: React.MouseEvent) => {
    const target = event.target as Element;
    let componentName = target.getAttribute('data-component-name');
    if (!componentName) {
      if (target instanceof SVGElement && target.className) {
        componentName = typeof target.className === 'string' 
          ? target.className 
          : target.className.baseVal;
      } else if (target instanceof HTMLElement && target.className) {
        componentName = target.className;
      }
    }
    if (!componentName) {
      componentName = target.tagName.toLowerCase();
    }
    if (target.getAttribute('data-reactroot')) {
      componentName = 'React Root';
    }
    console.log('Component clicked:', componentName);
    if ((componentName.indexOf('home-svg')!==-1 || componentName === 'tile') && isZoomed){
      handleSpecialCircleClickInternal(1.0, translateXRef.current, translateYRef.current);
      setSelectedTileGroup(null);
    }
  };

  const value = {
    handleSpecialCircleClick,
    selectedTileGroup,
    setSelectedTileGroup,
    isAnimating,
    handleComponentClick
  };

  useEffect(() => {
    if (selectedTileGroup && registry[selectedTileGroup]) {
      handleSpecialCircleClickInternal(
        registry[selectedTileGroup].scale || 1.0, 
        registry[selectedTileGroup].xTranslate || 0, 
        registry[selectedTileGroup].yTranslate || 0
      );
    } else if (translateXRef.current !== 0 || translateYRef.current !== 0) {
      handleSpecialCircleClickInternal(1.0, translateXRef.current, translateYRef.current);//zoom tf out
    }
  }, [selectedTileGroup]);

  return (
    <SvgZoomContext.Provider value={value}>
      {/* SVG composed of individual React components */}
      <div 
        ref={svgContainerRef}
        className="svg-container"
      >
        <svg
          width='100%'
          height='100%'
          viewBox={`0 0 ${width} ${height}`}
          style={{backgroundColor: 'transparent'}}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`home-svg ${animationClass}`}
          onClick={handleComponentClick}
        >
            {children}
        </svg>
      </div>
    </SvgZoomContext.Provider>
  );
}